/**
 * Farm Manager Model Provider - Gemini 2.5 Flash
 * 
 * Provides the Gemini 2.5 Flash model instance for the Farm Manager agent.
 * Supports thinking mode for improved reasoning on complex queries.
 * Generation parameters (temperature, maxOutputTokens, thinkingBudget)
 * are configurable through user settings.
 */

import { getAI, getGenerativeModel, GoogleAIBackend } from "firebase/ai";
import { getModularApp } from "../../firebase";
import { DEFAULT_FARM_MANAGER_SETTINGS } from "../../database/farmManagerSettings";
import { buildToolDeclarations } from "./toolDeclarations";

let aiInstance = null;
let generativeModel = null;
let currentSettings = null;

/**
 * Default model name — Gemini 2.5 Flash with thinking support.
 * Gemini 2.0 Flash retired June 2026; 2.5 Flash is the stable successor.
 */
const DEFAULT_MODEL = "gemini-2.5-flash";

/**
 * Builds the configuration for the Gemini model instance
 * @param {Object} settings - User settings containing generation parameters
 * @returns {Object} Model configuration
 */
function buildModelConfig(settings) {
        const modelSettings = settings?.model || DEFAULT_FARM_MANAGER_SETTINGS.model;
        const toolDeclarations = buildToolDeclarations();
        const modelName = modelSettings.name || DEFAULT_MODEL;
        
        const config = {
                model: modelName,
                generationConfig: {
                        temperature: modelSettings.temperature,
                        maxOutputTokens: modelSettings.maxOutputTokens,
                },
                tools: toolDeclarations.length > 0 ? [{ functionDeclarations: toolDeclarations }] : undefined,
        };

        // Enable thinking for 2.5+ models
        if (modelName.includes("2.5") || modelName.includes("3.")) {
                const budget = modelSettings.thinkingBudget;
                // -1 = dynamic (model decides), 0 = disabled, >0 = fixed budget
                // Default: dynamic thinking
                config.thinkingConfig = {
                        thinkingBudget: budget !== undefined ? budget : -1,
                };
        }

        return config;
}

/**
 * Gets or creates the Gemini model instance for the Farm Manager
 * @param {string} userId - User ID
 * @param {Object} settings - User settings containing generation parameters
 * @returns {Promise<Object>} Generative model instance
 */
export async function getFarmManagerModel(userId, settings) {
        const modelSettings = settings?.model || DEFAULT_FARM_MANAGER_SETTINGS.model;
        const settingsKey = JSON.stringify(modelSettings);
        
        if (currentSettings !== settingsKey) {
                generativeModel = null;
                currentSettings = settingsKey;
        }

        if (!aiInstance) {
                const app = await getModularApp();
                aiInstance = getAI(app, { backend: new GoogleAIBackend() });
        }

        if (!generativeModel) {
                const modelConfig = buildModelConfig(settings);
                generativeModel = getGenerativeModel(aiInstance, modelConfig);
                console.log(
                        `[modelProvider] Initialized ${modelConfig.model}` +
                        (modelConfig.thinkingConfig ? ` (thinking: budget=${modelConfig.thinkingConfig.thinkingBudget})` : "")
                );
        }

        return generativeModel;
}

/**
 * Returns the current default model name
 * @returns {string}
 */
export function getDefaultModelName() {
        return DEFAULT_MODEL;
}

/**
 * Resets the model instance cache
 */
export function resetFarmManagerModel() {
        aiInstance = null;
        generativeModel = null;
        currentSettings = null;
}
